/* eslint-disable */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Calendar, XCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adminApi } from "@/lib/adminApi";
import { STATUS_OPTIONS, STATUS_COLOR } from "@/components/admin/_shared";

export default function AdminJurisdictionsTab() {
  const [data, setData] = useState({ tax: [], privacy: [] });
  const [statusModal, setStatusModal] = useState(null); // { row, collection, newStatus }
  const [reviewUrls, setReviewUrls] = useState([]);
  const [reviewedIdx, setReviewedIdx] = useState(new Set());
  const [search, setSearch] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [annDraft, setAnnDraft] = useState({ banner_text: "", banner_priority: "INFORMATIONAL", scheduled_for: "" });

  const formatChecked = (iso) => {
    if (!iso) return "never";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };


  const load = async () => {
    const { data } = await adminApi.get("/admin/jurisdictions");
    setData(data);
    const r = await adminApi.get("/admin/weekly-review/checklist");
    setReviewUrls(r.data.urls);
    const a = await adminApi.get("/admin/scheduled-announcements");
    setAnnouncements(a.data.announcements || []);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const apply = async (sourceUrl, summary) => {
    if (!statusModal) return;
    try {
      await adminApi.post("/admin/jurisdictions/status", {
        collection: statusModal.collection,
        abbreviation: statusModal.row.abbreviation,
        new_status: statusModal.newStatus,
        verified_source_url: sourceUrl,
        verification_date: new Date().toISOString().slice(0, 10),
        change_summary: summary,
      });
      toast.success("Status updated");
      setStatusModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const markReviewedAll = async () => {
    try {
      await adminApi.post("/admin/weekly-review/mark-all");
      toast.success("All jurisdictions' verification dates updated");
      await load();
      setReviewedIdx(new Set());
    } catch {
      toast.error("Failed");
    }
  };

  const createAnnouncement = async () => {
    if (!annDraft.banner_text.trim()) return toast.error("Banner text required");
    if (!annDraft.scheduled_for) return toast.error("Scheduled date/time required");
    // 9.2 — Confirm broadcast scope (current impl is always "All Users")
    if (!window.confirm("⚠️ This will broadcast live to all active merchant dashboards. Confirm?")) {
      return;
    }
    try {
      // Convert local datetime-local value → ISO UTC
      const iso = new Date(annDraft.scheduled_for).toISOString();
      await adminApi.post("/admin/scheduled-announcements", {
        banner_text: annDraft.banner_text,
        banner_priority: annDraft.banner_priority,
        scheduled_for: iso,
      });
      toast.success("Announcement scheduled");
      setAnnDraft({ banner_text: "", banner_priority: "INFORMATIONAL", scheduled_for: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const cancelAnnouncement = async (id) => {
    if (!confirm("Cancel this scheduled announcement?")) return;
    try {
      await adminApi.post(`/admin/scheduled-announcements/${id}/cancel`);
      toast.success("Cancelled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!confirm("Are you sure you want to delete this announcement?")) return;
    try {
      await adminApi.delete(`/admin/scheduled-announcements/${id}`);
      toast.success("Announcement deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const allChecked = reviewedIdx.size === reviewUrls.length && reviewUrls.length > 0;
  const matchesSearch = (r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return r.state_name.toLowerCase().includes(q) || r.abbreviation.toLowerCase().includes(q);
  };

  const renderGroup = (label, rows, collection) => {
    const filtered = rows.filter(matchesSearch);
    return (
      <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] overflow-x-auto">
        <div className="p-3 border-b border-[var(--rg-border)] font-heading font-bold text-[var(--rg-text-strong)] flex items-center justify-between">
          <span>{label}</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)]">{filtered.length} / {rows.length}</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] border-b border-[var(--rg-border)]">
              <th className="py-2 px-3">State</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Change</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const status = r.status_value || (r.is_disabled ? "UNDER_REVIEW" : "ACTIVE");
              return (
                <tr key={r.abbreviation} className="border-b border-[var(--rg-border)]/60">
                  <td className="py-2 px-3 text-[var(--rg-text-strong)]">{r.state_name} <span className="text-[var(--rg-muted)] font-mono text-xs ml-1">({r.abbreviation})</span></td>
                  <td className="py-2 px-3">
                    <span className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider" style={{ color: STATUS_COLOR[status], borderColor: STATUS_COLOR[status], borderWidth: 1, borderStyle: "solid", backgroundColor: `${STATUS_COLOR[status]}1A` }}>
                      {status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <select
                      className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] text-xs h-7 rounded px-2"
                      value={status}
                      onChange={(e) => setStatusModal({ row: r, collection, newStatus: e.target.value })}
                      data-testid={`admin-status-${r.abbreviation}`}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={3} className="py-6 text-center text-[var(--rg-muted)]">No matches for &ldquo;{search}&rdquo;.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-status-tab">
      <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4">
        <Label className="text-[var(--rg-text)] text-xs uppercase flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Quick search</Label>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Jump to state (e.g., California, CA, TX)…"
          className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1.5 max-w-md"
          data-testid="admin-status-search"
        />
      </div>

      <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4">
        <div className="font-heading font-bold text-[var(--rg-text-strong)] mb-3">Weekly Review Checklist</div>
        <ul className="space-y-2 mb-3" data-testid="admin-weekly-checklist">
          {reviewUrls.map((u, i) => (
            <li key={u.url} className="flex items-center gap-2 text-sm flex-wrap" data-testid={`weekly-source-${i}`}>
              <input
                type="checkbox"
                checked={reviewedIdx.has(i)}
                onChange={(e) => {
                  const next = new Set(reviewedIdx);
                  if (e.target.checked) {
                    next.add(i);
                    checkSource(u.url);
                  } else {
                    next.delete(i);
                  }
                  setReviewedIdx(next);
                }}
                data-testid={`weekly-source-checkbox-${i}`}
              />
              <a href={u.url} target="_blank" rel="noopener noreferrer" className="text-[var(--rg-text)] hover:text-[var(--rg-text-strong)]">{u.label}</a>
              {u.last_checked && (
                <span
                  className="text-[10px] text-[var(--rg-muted)] font-mono ml-auto"
                  data-testid={`weekly-source-last-checked-${i}`}
                >
                  Last checked: {formatChecked(u.last_checked)}
                </span>
              )}
            </li>
          ))}
        </ul>
        {allChecked && (
          <Button size="sm" onClick={markReviewedAll} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="admin-mark-reviewed">
            Mark All Reviewed — Update Verification Dates
          </Button>
        )}
      </div>

      {renderGroup("Sales Tax Nexus Rules", data.tax, "tax")}
      {renderGroup("State Privacy Laws", data.privacy, "privacy")}

      {/* ---- Scheduled Announcements Queue ---- */}
      <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4" data-testid="admin-scheduled-queue">
        <div className="font-heading font-bold text-[var(--rg-text-strong)] mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[var(--rg-amber)]" /> Scheduled Announcements Queue
        </div>
        <p className="text-xs text-[var(--rg-text)] mb-3">
          Draft a banner now, publish later. The cron checks every 5 minutes; on or after the scheduled time the banner replaces the currently active one.
        </p>
        <div className="grid sm:grid-cols-4 gap-2 items-end mb-3">
          <div className="sm:col-span-2">
            <Label className="text-[var(--rg-text)] text-xs uppercase">Banner text (max 160)</Label>
            <Input
              value={annDraft.banner_text}
              maxLength={160}
              onChange={(e) => setAnnDraft({ ...annDraft, banner_text: e.target.value })}
              className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]"
              data-testid="ann-banner-text"
            />
            <div className="text-[10px] text-[var(--rg-muted)] mt-0.5">{annDraft.banner_text.length}/160</div>
          </div>
          <div>
            <Label className="text-[var(--rg-text)] text-xs uppercase">Priority</Label>
            <select
              value={annDraft.banner_priority}
              onChange={(e) => setAnnDraft({ ...annDraft, banner_priority: e.target.value })}
              className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] text-sm rounded px-2 py-1.5 w-full"
              data-testid="ann-priority"
            >
              <option value="INFORMATIONAL">INFORMATIONAL</option>
              <option value="IMPORTANT">IMPORTANT</option>
              <option value="URGENT">URGENT</option>
            </select>
          </div>
          <div>
            <Label className="text-[var(--rg-text)] text-xs uppercase">Publish at</Label>
            <Input
              type="datetime-local"
              value={annDraft.scheduled_for}
              onChange={(e) => setAnnDraft({ ...annDraft, scheduled_for: e.target.value })}
              className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]"
              data-testid="ann-scheduled-for"
            />
          </div>
        </div>
        <Button size="sm" onClick={createAnnouncement} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="ann-create">
          <Plus className="w-4 h-4 mr-1.5" /> Schedule
        </Button>

        <div className="mt-4 rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] border-b border-[var(--rg-border)]">
                <th className="py-2 px-3">Scheduled For</th>
                <th className="py-2 px-3">Priority</th>
                <th className="py-2 px-3">Text</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((a) => (
                <tr key={a.id} className="border-b border-[var(--rg-border)]/60" data-testid={`ann-row-${a.id}`}>
                  <td className="py-2 px-3 text-[var(--rg-text)] font-mono text-xs whitespace-nowrap">{a.scheduled_for?.slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 px-3"><span className="text-[10px] font-bold uppercase tracking-wider text-[var(--rg-amber)]">{a.banner_priority}</span></td>
                  <td className="py-2 px-3 text-[var(--rg-text-strong)]">{a.banner_text}</td>
                  <td className="py-2 px-3 font-mono text-xs uppercase">
                    {a.status === "scheduled" && <span className="text-[var(--rg-amber)]">scheduled</span>}
                    {a.status === "published" && <span className="text-[var(--rg-sage)]">published</span>}
                    {a.status === "cancelled" && <span className="text-[var(--rg-red)]">cancelled</span>}
                  </td>
                  <td className="py-2 px-3 flex gap-1.5 flex-wrap">
                    {a.status === "scheduled" && (
                      <Button size="sm" variant="outline" onClick={() => cancelAnnouncement(a.id)} className="border-[var(--rg-amber)] text-[var(--rg-amber)] hover:bg-[var(--rg-amber-soft)] text-xs h-7" data-testid={`ann-cancel-${a.id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => deleteAnnouncement(a.id)} className="border-[var(--rg-red)] text-[var(--rg-red)] hover:bg-[var(--rg-red-soft)] text-xs h-7" data-testid={`ann-delete-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  </td>
                </tr>
              ))}
              {announcements.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-[var(--rg-muted)]">No scheduled announcements.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!statusModal} onOpenChange={(o) => !o && setStatusModal(null)}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="admin-status-modal">
          <DialogHeader>
            <DialogTitle>
              Change status: {statusModal?.row?.abbreviation} → {statusModal?.newStatus?.replace("_", " ")}
            </DialogTitle>
          </DialogHeader>
          <StatusForm
            key={`${statusModal?.collection || ""}-${statusModal?.row?.abbreviation || ""}`}
            defaultUrl={statusModal?.row?.official_url || ""}
            onSubmit={apply}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusForm({ defaultUrl = "", onSubmit }) {
  // Auto-populate the Verified Source URL with the row's official_url from Tab 1.
  // The admin can override before applying. Reset on each modal open via `key` on parent.
  const [url, setUrl] = useState(defaultUrl);
  const [summary, setSummary] = useState("");
  return (
    <div className="space-y-3">
      <Label className="text-[var(--rg-text)] text-xs uppercase">Verified source URL</Label>
      <Input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://..."
        className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]"
        data-testid="status-source-url"
      />
      {defaultUrl && url === defaultUrl && (
        <p className="text-[10px] text-[var(--rg-muted)] font-mono -mt-2" data-testid="status-source-prefill-hint">
          Pre-filled from Database Management. Edit to verify against a newer source.
        </p>
      )}
      <Label className="text-[var(--rg-text)] text-xs uppercase">Change summary</Label>
      <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="status-summary" />
      <Button onClick={() => onSubmit(url, summary)} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="status-apply">
        Apply
      </Button>
    </div>
  );
}
